import React, { useEffect, useState, useContext } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { ModalHeader, ModalTitle, ModalBody, ModalFooter } from '@atlaskit/modal-dialog';
import Textfield from '@atlaskit/textfield';

import {
	EDIT_PAGE_PUBLISH_EXPERIENCE,
	ONBOARDING_EDITOR_PUBLISH_MODAL_PROMPT_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useEditorModalContext, ALLOWED_EDITOR_MODAL } from '@confluence/editor-modal';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { useEditorOnboardingEvents } from './EditorOnboardingAnalytics.tsx';
import { ModalDescription } from './styled/index.ts';

type EditorModalPromptProps = {
	onTitleChange: (value: string) => void | null;
	handlePublish: () => Promise<any> | void;
};

const i18n = defineMessages({
	editorModalTitle: {
		id: 'onboarding-editor.modal.title',
		defaultMessage: 'This page needs a title',
		description:
			'Title of the modal dialog that appears when user tries to publish a page without a title during onboarding.',
	},
	editorModalDescription: {
		id: 'onboarding-editor.modal.description',
		defaultMessage: 'Before you can publish this page, you’ll need to give it a title.',
		description:
			'Body text of the modal explaining that a page title is required before publishing.',
	},
	editorModalPlaceholder: {
		id: 'onboarding-editor.modal.placeholder',
		defaultMessage: 'Enter title',
		description: 'Placeholder text inside the text input field where user enters the page title.',
	},
	editorModalPublish: {
		id: 'onboarding-editor.modal.button.publish',
		defaultMessage: 'Publish',
		description:
			'Label for the button that publishes the page after user has entered a title. The button is disabled until a title is provided.',
	},
	editorModalClose: {
		id: 'onboarding-editor.modal.button.close',
		defaultMessage: 'Close',
		description: 'Label for the button that closes the modal without publishing the page.',
	},
});

export const OnboardingEditorModalPrompt: React.FC<EditorModalPromptProps> = (props) => {
	const { onTitleChange, handlePublish } = props;
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [title, setTitle] = useState<string>('');
	const { setOnboardingState } = useSetOnboardingState();
	const { modal, hideEditorModal } = useEditorModalContext();
	const showModal = modal?.[ALLOWED_EDITOR_MODAL.ONBOARDING_EDITOR_MODAL] || false;
	const { fireEditorModalPromptClosed, fireEditorModalPromptPublished } =
		useEditorOnboardingEvents();
	const intl = useIntl();
	const { Modal } = useChoreographerAssets();

	useEffect(() => {
		experienceTracker.start({
			name: ONBOARDING_EDITOR_PUBLISH_MODAL_PROMPT_EXPERIENCE,
			collect(events, experience) {
				// Fail or abort ONBOARDING_EDITOR_PUBLISH_MODAL_PROMPT_EXPERIENCE as soon as EDIT_PAGE_PUBLISH_EXPERIENCE fails or aborts:
				experience.stopOn(
					events.find(
						({ action, name }) =>
							(action === 'taskFail' || action === 'taskAbort') &&
							name === EDIT_PAGE_PUBLISH_EXPERIENCE,
					),
				);
			},
		});
	}, [experienceTracker]);

	const endExperienceTracker = () => {
		experienceTracker.succeed({
			name: ONBOARDING_EDITOR_PUBLISH_MODAL_PROMPT_EXPERIENCE,
		});
	};

	const onClose = () => {
		void hideEditorModal(ALLOWED_EDITOR_MODAL.ONBOARDING_EDITOR_MODAL);
		fireEditorModalPromptClosed();
		endExperienceTracker();
	};

	const onClickPublish = () => {
		// There needs to be a title before publishing is possible
		onTitleChange(title);

		return Promise.resolve(hideEditorModal(ALLOWED_EDITOR_MODAL.ONBOARDING_EDITOR_MODAL))
			.then(() => {
				void handlePublish();
			})
			.then(() => {
				// set hasUserPublished to true
				void setOnboardingState({
					key: EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
					value: 'true',
				});

				fireEditorModalPromptPublished();
				endExperienceTracker();
			});
	};

	return showModal ? (
		<Modal
			key="onboarding-editor-modal"
			onClose={onClose}
			shouldCloseOnEscapePress
			shouldCloseOnOverlayClick
			width={480}
			messageType="transactional"
			messageId="onboarding-editor-modal"
		>
			<ModalHeader hasCloseButton>
				<ModalTitle>
					<FormattedMessage {...i18n.editorModalTitle} />
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<ModalDescription>
					<FormattedMessage {...i18n.editorModalDescription} />
				</ModalDescription>
				<Textfield
					testId="editor-modal-prompt-textfield"
					autoFocus
					onChange={(e) => {
						setTitle((e.target as HTMLInputElement).value);
					}}
					placeholder={intl.formatMessage(i18n.editorModalPlaceholder)}
				/>
			</ModalBody>
			<ModalFooter>
				<Button
					onClick={onClose}
					autoFocus={false}
					appearance="subtle"
					testId="editor-modal-prompt-close-button"
				>
					<FormattedMessage {...i18n.editorModalClose} />
				</Button>
				<Button
					onClick={onClickPublish}
					type="submit"
					autoFocus={false}
					onSubmit={onClickPublish}
					isDisabled={title.length === 0}
					appearance="primary"
					testId="editor-modal-prompt-publish-button"
				>
					<FormattedMessage {...i18n.editorModalPublish} />
				</Button>
			</ModalFooter>
		</Modal>
	) : null;
};
